/**
 * Product sticky secondary nav:
 * - show when main-product leaves the viewport
 * - anchor scroll + active state
 * - sync price from main product price node / variantChange
 * - ATC triggers main product form submit button
 */
(function () {
  const HEADER_OFFSET = 76;
  const roots = new Set();

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function normalizeId(value) {
    return String(value || '')
      .trim()
      .replace(/^#/, '')
      .toLowerCase()
      .replace(/[^a-z0-9\-_]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function getMainProductEl() {
    return (
      document.querySelector('product-info[id^="MainProduct-"]') ||
      document.querySelector('#MainProduct-' + getMainSectionId()) ||
      document.querySelector('product-info[data-section]')
    );
  }

  function getMainSectionId() {
    const info = document.querySelector('product-info[data-section]');
    return info?.dataset?.section || null;
  }

  function getStickyOffset(nav) {
    const topBar = nav?.querySelector('.product-sticky-nav__top') || nav;
    const navHeight = topBar?.offsetHeight || 56;
    return HEADER_OFFSET + navHeight + 8;
  }

  function findTarget(targetId) {
    const id = normalizeId(targetId);
    if (!id) return null;

    return (
      document.getElementById(id) ||
      document.querySelector(`[data-sticky-nav-anchor="${CSS.escape(id)}"]`) ||
      null
    );
  }

  function setVisible(root, visible) {
    root.classList.toggle('is-visible', visible);
    root.setAttribute('aria-hidden', visible ? 'false' : 'true');
    // Re-enable pointer events on the Shopify section wrapper while visible
    const sectionEl = root.closest('.shopify-section');
    if (sectionEl) {
      sectionEl.classList.toggle('product-sticky-nav-section--active', visible);
    }
  }

  function stripPriceExtras(node) {
    if (!node || !node.querySelectorAll) return node;
    node
      .querySelectorAll('.badge, .price__badge-sale, .price__badge-sold-out, .unit-price, .price__caption, .volume-pricing-note')
      .forEach((el) => el.remove());
    node.querySelectorAll('.price').forEach((el) => {
      el.classList.remove('price--show-badge');
    });
    return node;
  }

  function syncPrice(root) {
    const priceSlot = root.querySelector('[data-sticky-nav-price]');
    if (!priceSlot) return;

    const sectionId = getMainSectionId();
    const source = sectionId ? document.getElementById(`price-${sectionId}`) : null;
    if (!source) return;

    const clone = stripPriceExtras(source.cloneNode(true));
    const hasPriceText = Boolean(clone.querySelector('.price-item'));
    if (!hasPriceText) return;

    priceSlot.innerHTML = clone.innerHTML;
  }

  function syncAtcState(root) {
    const atc = root.querySelector('[data-sticky-nav-atc]');
    if (!atc) return;
    if (!atc.dataset.defaultLabel) {
      atc.dataset.defaultLabel = atc.textContent.trim();
    }
    const sectionId = getMainSectionId();
    if (!sectionId) return;
    const mainSubmit = document.querySelector(
      `#product-form-${sectionId} [type="submit"].product-form__submit, #product-form-${sectionId} button.product-form__submit`
    );
    if (!mainSubmit) return;
    atc.disabled = Boolean(mainSubmit.disabled);
    if (mainSubmit.disabled) {
      atc.textContent = mainSubmit.textContent.trim() || atc.dataset.defaultLabel;
    } else {
      atc.textContent = atc.dataset.defaultLabel;
    }
  }

  function setActiveLink(root, targetId) {
    const activeId = normalizeId(targetId);
    root.querySelectorAll('[data-sticky-nav-target]').forEach((link) => {
      const isActive = normalizeId(link.getAttribute('data-sticky-nav-target')) === activeId;
      link.classList.toggle('is-active', isActive);
    });
  }

  function unlockActiveTracking(root) {
    root._activeLocked = false;
    root._activeLockId = null;
    if (root._activeUnlockTimer) {
      window.clearTimeout(root._activeUnlockTimer);
      root._activeUnlockTimer = null;
    }
    if (root._activeUnlockHandler) {
      window.removeEventListener('scrollend', root._activeUnlockHandler);
      root._activeUnlockHandler = null;
    }
  }

  function lockActiveTracking(root, targetId, durationMs) {
    unlockActiveTracking(root);
    root._activeLocked = true;
    root._activeLockId = normalizeId(targetId);
    setActiveLink(root, targetId);

    const unlock = () => {
      unlockActiveTracking(root);
      updateActiveFromScroll(root);
    };

    root._activeUnlockHandler = unlock;
    // Prefer scrollend when available; always keep a timeout fallback.
    window.addEventListener('scrollend', unlock, { once: true });
    root._activeUnlockTimer = window.setTimeout(unlock, durationMs);
  }

  function scrollToTarget(root, targetId) {
    const target = findTarget(targetId);
    if (!target) {
      console.warn('[product-sticky-nav] target not found:', targetId);
      return;
    }

    const top = Math.max(0, target.getBoundingClientRect().top + window.pageYOffset - getStickyOffset(root));
    const distance = Math.abs(window.pageYOffset - top);
    const reduced = prefersReducedMotion();
    // Rough duration for smooth scroll lock window
    const lockMs = reduced ? 50 : Math.min(1200, Math.max(350, distance * 0.6));

    lockActiveTracking(root, targetId, lockMs);
    window.scrollTo({
      top,
      behavior: reduced ? 'auto' : 'smooth',
    });
  }

  function bindNavClicks(root) {
    root.querySelectorAll('[data-sticky-nav-target]').forEach((link) => {
      if (link.dataset.bound === 'true') return;
      link.dataset.bound = 'true';
      link.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const targetId = link.getAttribute('data-sticky-nav-target');
        scrollToTarget(root, targetId);
      });
    });
  }

  function bindAtc(root) {
    const atc = root.querySelector('[data-sticky-nav-atc]');
    if (!atc || atc.dataset.bound === 'true') return;
    atc.dataset.bound = 'true';

    atc.addEventListener('click', () => {
      const sectionId = getMainSectionId();
      if (!sectionId) return;
      const mainSubmit = document.querySelector(
        `#product-form-${sectionId} [type="submit"].product-form__submit, #product-form-${sectionId} button.product-form__submit`
      );
      if (mainSubmit && !mainSubmit.disabled) mainSubmit.click();
    });
  }

  function observeMainProduct(root) {
    const main = getMainProductEl();
    if (!main) {
      setVisible(root, false);
      return;
    }

    const updateVisibility = () => {
      const rect = main.getBoundingClientRect();
      const shouldShow = rect.bottom <= HEADER_OFFSET;
      setVisible(root, shouldShow);
    };

    const observer = new IntersectionObserver(() => updateVisibility(), {
      root: null,
      threshold: [0, 0.01, 0.1, 1],
      rootMargin: `-${HEADER_OFFSET}px 0px 0px 0px`,
    });

    observer.observe(main);
    root._mainObserver = observer;
    root._visibilityHandler = updateVisibility;
    window.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility, { passive: true });
    updateVisibility();
  }

  function updateActiveFromScroll(root) {
    if (!root.classList.contains('is-visible')) return;
    // Ignore scroll-driven updates while a click-to-scroll animation is running
    if (root._activeLocked) {
      if (root._activeLockId) setActiveLink(root, root._activeLockId);
      return;
    }

    const links = Array.from(root.querySelectorAll('[data-sticky-nav-target]'));
    const offset = getStickyOffset(root);
    let best = null;

    links.forEach((link) => {
      const id = normalizeId(link.getAttribute('data-sticky-nav-target'));
      const el = findTarget(id);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.top <= offset + 32) {
        // Prefer the last section that has crossed the sticky line
        if (!best || rect.top >= best.top) best = { id, top: rect.top };
      }
    });

    if (!best) {
      for (const link of links) {
        const id = normalizeId(link.getAttribute('data-sticky-nav-target'));
        if (findTarget(id)) {
          best = { id, top: 0 };
          break;
        }
      }
    }

    if (best?.id) setActiveLink(root, best.id);
  }

  function observeSections(root) {
    const onScroll = () => updateActiveFromScroll(root);
    root._sectionScrollHandler = onScroll;
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    onScroll();
  }

  function bindVariantListeners(root) {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      root._unsubVariant = subscribe(PUB_SUB_EVENTS.variantChange, () => {
        requestAnimationFrame(() => {
          syncPrice(root);
          syncAtcState(root);
          setTimeout(() => {
            syncPrice(root);
            syncAtcState(root);
          }, 80);
        });
      });
    }

    const sectionId = getMainSectionId();
    const source = sectionId ? document.getElementById(`price-${sectionId}`) : null;
    if (source && typeof MutationObserver !== 'undefined') {
      const mo = new MutationObserver(() => {
        syncPrice(root);
        syncAtcState(root);
      });
      mo.observe(source, { childList: true, subtree: true, characterData: true });
      root._priceObserver = mo;
    }
  }

  function destroy(root) {
    unlockActiveTracking(root);
    root._mainObserver?.disconnect();
    root._priceObserver?.disconnect();
    if (typeof root._unsubVariant === 'function') root._unsubVariant();
    if (root._visibilityHandler) {
      window.removeEventListener('scroll', root._visibilityHandler);
      window.removeEventListener('resize', root._visibilityHandler);
    }
    if (root._sectionScrollHandler) {
      window.removeEventListener('scroll', root._sectionScrollHandler);
      window.removeEventListener('resize', root._sectionScrollHandler);
    }
    roots.delete(root);
  }

  function initRoot(root) {
    if (!root || roots.has(root)) return;
    roots.add(root);

    bindNavClicks(root);
    bindAtc(root);
    syncPrice(root);
    syncAtcState(root);
    observeMainProduct(root);
    observeSections(root);
    bindVariantListeners(root);
  }

  function initAll() {
    document.querySelectorAll('[data-product-sticky-nav]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const scope = event.target;
    scope.querySelectorAll?.('[data-product-sticky-nav]')?.forEach((el) => {
      destroy(el);
      initRoot(el);
    });
    if (scope.matches?.('[data-product-sticky-nav]')) {
      destroy(scope);
      initRoot(scope);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-product-sticky-nav]')?.forEach(destroy);
    if (event.target.matches?.('[data-product-sticky-nav]')) destroy(event.target);
  });
})();
